import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { setSession } from '../lib/auth';
import { API_BASE } from '../lib/api';
import { generateCodeVerifier, deriveCodeChallenge, rememberVerifier } from '../lib/pkce';

const IS_DEV = import.meta.env.DEV;

export function Login() {
  const navigate = useNavigate();
  const [userId, setUserId] = useState('test-user');
  const [orgId, setOrgId] = useState('test-org');
  const [role, setRole] = useState<'member' | 'admin' | 'owner'>('member');
  const [error, setError] = useState<string | null>(null);

  const handleDevLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!IS_DEV) {
      setError('Dev login is disabled in production builds.');
      return;
    }
    const token = `dev:${userId}:${orgId}:${role}`;
    setSession({
      token,
      userId,
      email: `${userId}@dev.local`,
      orgId,
      role,
    });
    navigate('/library', { replace: true });
  };

  const [busy, setBusy] = useState(false);

  const handleProdLogin = async () => {
    setError(null);
    setBusy(true);
    try {
      const verifier = generateCodeVerifier();
      const challenge = await deriveCodeChallenge(verifier);
      const redirectUri = `${window.location.origin}/auth/callback`;
      const res = await fetch(`${API_BASE}/auth/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code_challenge: challenge, redirect_uri: redirectUri }),
      });
      if (!res.ok) {
        let detail = '';
        try {
          detail = (await res.json()).message ?? '';
        } catch {
          // ignore
        }
        setError(`Sign-in setup failed (HTTP ${res.status}${detail ? ': ' + detail : ''}).`);
        setBusy(false);
        return;
      }
      const { authorization_url, state } = (await res.json()) as {
        authorization_url: string;
        state: string;
      };
      rememberVerifier(verifier, state);
      window.location.href = authorization_url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in start failed');
      setBusy(false);
    }
  };

  return (
    <main className="flex h-full items-center justify-center bg-surface-2 p-4">
      <div className="w-full max-w-md">
        <div className="mb-4 text-center">
          <h1 className="font-mono text-2xl">skillsou</h1>
          <p className="mt-2 text-sm text-text-muted">
            Edit AI skills in your browser. Share via GitHub. Sync to Claude
            Code, Codex, and Cursor with one CLI.
          </p>
        </div>

        <div
          className="rounded-lg border bg-canvas p-6 shadow-sm"
          style={{ boxShadow: '0 20px 60px rgba(0,0,0,.08), 0 0 0 1px rgba(0,0,0,.04)' }}
        >
          <h2 className="text-sm font-semibold">Sign in</h2>
          <p className="mt-1 text-xs text-text-subtle">
            We use WorkOS — no password, just your work email.
          </p>

          <button
            type="button"
            onClick={handleProdLogin}
            disabled={busy}
            className="btn btn-primary mt-4 w-full justify-center"
          >
            {busy ? 'Redirecting…' : 'Sign in with WorkOS'}
          </button>

          {IS_DEV && (
            <>
              <div className="my-4 flex items-center gap-3 text-[10px] uppercase tracking-wider text-text-subtle">
                <span className="h-px flex-1 bg-hairline" />
                <span>dev shortcut</span>
                <span className="h-px flex-1 bg-hairline" />
              </div>
              <form onSubmit={handleDevLogin} className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <label className="block text-[11px] text-text-muted">
                    user id
                    <input
                      type="text"
                      value={userId}
                      onChange={(e) => setUserId(e.target.value)}
                      className="mt-1 block w-full rounded border bg-surface px-2 py-1 font-mono text-[12px]"
                    />
                  </label>
                  <label className="block text-[11px] text-text-muted">
                    org id
                    <input
                      type="text"
                      value={orgId}
                      onChange={(e) => setOrgId(e.target.value)}
                      className="mt-1 block w-full rounded border bg-surface px-2 py-1 font-mono text-[12px]"
                    />
                  </label>
                </div>
                <label className="block text-[11px] text-text-muted">
                  role
                  <select
                    value={role}
                    onChange={(e) =>
                      setRole(e.target.value as 'member' | 'admin' | 'owner')
                    }
                    className="mt-1 block w-full rounded border bg-surface px-2 py-1 font-mono text-[12px]"
                  >
                    <option value="member">member</option>
                    <option value="admin">admin</option>
                    <option value="owner">owner</option>
                  </select>
                </label>
                <button type="submit" className="btn w-full justify-center">
                  Dev sign in
                </button>
              </form>
            </>
          )}

          {error && (
            <p className="mt-3 rounded border bg-danger-soft p-2 text-xs text-[var(--color-danger)]">
              {error}
            </p>
          )}
        </div>

        <p className="mt-4 text-center text-[11px] text-text-subtle">
          v1 — editor + AI critic + GitHub share + CLI sync
        </p>
      </div>
    </main>
  );
}
